import { useEffect, useState, useCallback, type Dispatch, type SetStateAction, type PropsWithChildren, type default as React } from 'react';

import { AuthType } from 'constants/constants';

import { Box, Grid, Button, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, Alert } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { ActionButton, CanselButton,FileImportData, UploadButton } from 'components/commonparts';
import { clearMessageInfo } from 'types/types'
import { convertFromDateTime } from 'utils/convertFromLocalDate'

import DownloadCsvButton from '../Buttons/DownloadCsvButton'
import DownloadExcelButton from '../Buttons/DownloadExcelButton'
import MessageInfo from '../MessageInfo/MessageInfo'
import 'utils/mst.scss'

import type { ResponseResult } from 'api-client'

type MasterSearchFromProps = {
  onFileDownLoad?: () => void  // ファイルダウンロード（CSV・Excel）ボタン押下
  onDoUpdate?: (opts?: { skipReload?: boolean }) => void | Promise<void>  // 更新ボタン押下（skipReload: 発注推奨数ダイアログOK時は再読込しない）
  onReload?: () => void  // 初期化ボタン押下（編集中の場合の確認後、OKならイベントが発火するので、画面側ではチェック不要）
  onImportData?: (header: any[], data: any[]) => Promise<void>;   // ファイルインポート後、引数にヘッダーとデータが来る
  isDownload?: boolean  // ダウンロードボタンを出すか出さないか。デフォルトで出すにしてあるので画面側では設定しなくてＯＫ
  isImport?: boolean  // アップロードボタンを出すか出さないか。デフォルトで出すにしてあるので画面側では設定しなくてＯＫ
  isUpload?: boolean  // アップロードボタンを出すか出さないか。デフォルトで出すにしてあるので画面側では設定しなくてＯＫ
  result?: ResponseResult | undefined // 検索エリアのメッセージ表示エリアの設定
  discardChangeDialogOpen?:boolean  // 編集中に検索やリセットをするか確認するダイアログを表示する
  forbiddenDownloadDialogOpen?:boolean
  hasDataEdited: boolean  // 編集中のデータがあるか？
  setDownloadMode: Dispatch<SetStateAction<string>> // ＣＳＶかExcelか、どちらのダウンロードボタンを押したのか
  importRequiredFields: string[]; // Excelファイルインポートの際に、プライマリキーの必須項目チェックをする
  headerJp?: string[];
  lastUpdateDatetime: Date | null // 検索データの最新更新時間
  onFileUpload?: (file: File) => Promise<void>  // ファイルアップロードボタン押下
  isUploading?: boolean // ファイルアップロード処理中
  onOrderRecommend?: () => void  // 発注推奨数ボタン押下
  stockCenterOrderRecommend?: boolean  // 倉庫別発注商品一覧の発注推奨数ボタン押下
  isOrderRecommend?: boolean  // 発注推奨数ボタンを出すか出さないか
  apiDownload?: boolean  // APIからダウンロードするかどうか
  onDownloadApiData?: (mode: string) => void  // APIからダウンロードする
  mismatchedStockItemCodes?: string[]  // 在庫数量の不一致があるアイテムコードのリスト
  updateButtonText?: string  // 更新ボタンのテキスト
  optimizeButtonText?: string  // 積載率最適化ボタンのテキスト
  /** 商品絞り込み時は登録・発注推奨数再計算を無効化する（例: 倉庫別発注商品一覧で itemCodes 指定時） */
  disableRegisterAndOrderRecommend?: boolean
  /** 初期化ボタンを無効化（例: ローカル検索条件未保存時） */
  disableReloadButton?: boolean
  /** CSV / Excel ダウンロードを無効化 */
  disableDownloadButtons?: boolean
}

function MasterSearchFrom({
  onFileDownLoad,
  onDoUpdate,
  onReload,
  onImportData,
  isDownload = true,
  isImport = true,
  isUpload = false,
  result,
  children,
  discardChangeDialogOpen = false,
  forbiddenDownloadDialogOpen = false,
  hasDataEdited = false,
  setDownloadMode,
  importRequiredFields=[],
  headerJp=[],
  lastUpdateDatetime,
  onFileUpload,
  isUploading,
  onOrderRecommend,
  isOrderRecommend = false,
  apiDownload = false,
  onDownloadApiData,
  mismatchedStockItemCodes = [],
  updateButtonText,
  optimizeButtonText = "登録・発注推奨数再計算",
  stockCenterOrderRecommend,
  disableRegisterAndOrderRecommend = false,
  disableReloadButton = false,
  disableDownloadButtons = false,
}: PropsWithChildren<MasterSearchFromProps>) {
  const { loginUserInfo } = useAuthContext()

  const [shouldResetUploadButton, setShouldResetUploadButton] = useState<boolean>(false);
  const [showDiscardChangeDialog, setShowDiscardChangeDialog] = useState<boolean>(false)
  const [showForbiddenDownloadDialog, setForbiddenDownloadDialog] = useState<boolean>(false)
  // 発注推奨数ダイアログ用（未使用のためコメントアウト）
  // const [showOrderRecommendDialog, setShowOrderRecommendDialog] = useState<boolean>(false)
  // const [showOrderRecommendDiscardDialog, setShowOrderRecommendDiscardDialog] = useState<boolean>(false)
  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)

  useEffect(() => {
    setResultMessage(result)
  }, [result])

  useEffect(() => {
    if (hasDataEdited)
      setShowDiscardChangeDialog(true)
  }, [discardChangeDialogOpen])

  useEffect(() => {
    setForbiddenDownloadDialog(forbiddenDownloadDialogOpen)
  }, [forbiddenDownloadDialogOpen])

  // useEffect(() => {
  // }, [showDiscardChangeDialog])

  const handleReload = () => {
    setShowDiscardChangeDialog(false)
    setShouldResetUploadButton((prev) => !prev); 
    onReload?.();
  };
  const handleCanselButtonClick = () => {
    if (hasDataEdited) {
      setShowDiscardChangeDialog(true)
      return
    } 

    handleReload();
  };

  const handleCsvDownLoad = () => {
    if (hasDataEdited) {
      setForbiddenDownloadDialog(true)
      return
    } 
    setResultMessage(clearMessageInfo())
    setDownloadMode('csv')
    onFileDownLoad?.();
    setShouldResetUploadButton((prev) => !prev); 
  };

  const handleExcelDownLoad = () => {
    if (hasDataEdited) {
      setForbiddenDownloadDialog(true)
      return
    }
    setDownloadMode('xlsx')
    setResultMessage(clearMessageInfo())

    onFileDownLoad?.();
    setShouldResetUploadButton((prev) => !prev); 
  };

  const handleCsvDownLoadApi = () => {
    if (hasDataEdited) {
      setForbiddenDownloadDialog(true)
      return
    } 
    setResultMessage(clearMessageInfo())
    setDownloadMode('csv')
    onDownloadApiData?.("csv");
    setShouldResetUploadButton((prev) => !prev); 
  };

  const handleExcelDownLoadApi = () => {
    if (hasDataEdited) {
      setForbiddenDownloadDialog(true)
      return
    }
    setDownloadMode('xlsx')
    setResultMessage(clearMessageInfo())

    onDownloadApiData?.("xlsx");
    setShouldResetUploadButton((prev) => !prev); 
  };
  

  const resetUploadButton = () => {
    setShouldResetUploadButton((prev) => !prev);
  };

  const handleDiscardChangeDialogClose = useCallback(() => {
    setShowDiscardChangeDialog(false)
  }, [])

  const handleForbiddenDownloadDialogClose = useCallback(() => {
    setForbiddenDownloadDialog(false)
  }, [])

  // 発注推奨数ダイアログ用ハンドラ（未使用のためコメントアウト）
  // const handleOrderRecommendDialogClose = useCallback(() => {
  //   setShowOrderRecommendDialog(false)
  // }, [])
  const handleOrderRecommendConfirm = useCallback(async () => {
    await onDoUpdate?.({ skipReload: true })
    ;(onOrderRecommend)?.()
  }, [onDoUpdate, onOrderRecommend])
  // const handleOrderRecommendDiscardDialogClose = useCallback(() => {
  //   setShowOrderRecommendDiscardDialog(false)
  // }, [])
  // const handleOrderRecommendDiscardConfirm = useCallback(() => {
  //   setShowOrderRecommendDiscardDialog(false)
  //   onOrderRecommend?.();
  // }, [onOrderRecommend])

  const handleOrderRecommendClick = () => {
    if (disableRegisterAndOrderRecommend) {
      return
    }
    if (stockCenterOrderRecommend && hasDataEdited) {
      handleOrderRecommendConfirm();
    } else {
      onOrderRecommend?.();
    }
  }

  return (
    <Paper variant="outlined" sx={{ '& .MuiInputLabel-formControl': { marginTop: '0.25rem' } }}>
      <Grid container>
        <Grid size={{ xs: 12 }} container>
          {children}
        </Grid>
        <Grid size={{ xs: 12 }} justifyContent="space-between" alignItems="center" container sx={{ marginTop: '0.25rem' }}>
          <Grid>
            <MessageInfo result={resultMessage} />
          </Grid>

          <Grid>
            <Box display="flex" alignItems="center" gap={0.5}>
              {mismatchedStockItemCodes && Array.isArray(mismatchedStockItemCodes) && mismatchedStockItemCodes.length > 0 && (
                <Alert severity="warning" className="mismatch-warning-alert">
                  在庫を再計算しました。保存してください
                </Alert>
              )}
              {loginUserInfo && (loginUserInfo?.authType !== AuthType.ReadOnly) && (
                <>
                  <ActionButton 
                    caption={updateButtonText}
                    onClick={() => { 
                      onDoUpdate?.(); 
                    }} 
                    disabled={false} 
                  />
                  <CanselButton
                    onClick={handleCanselButtonClick}
                    caption="初期化"
                    disabled={disableReloadButton}
                  />
                </>
              )}
              
              {isDownload && (
                <>
                  <DownloadCsvButton
                    onClick={apiDownload ? handleCsvDownLoadApi : handleCsvDownLoad}
                    disabled={disableDownloadButtons}
                  />
                  <DownloadExcelButton
                    onClick={apiDownload ? handleExcelDownLoadApi : handleExcelDownLoad}
                    disabled={disableDownloadButtons}
                  />
                  {/* <FileDownloadData fileName="自社センターマスタ" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} /> */}

                </>
              )}
              { isImport && onImportData && loginUserInfo?.authType !== AuthType.ReadOnly &&        
                <FileImportData 
                  onImportData={onImportData} 
                  shouldReset={shouldResetUploadButton}
                  resetCallback={resetUploadButton}
                  requiredFields={importRequiredFields}
                  headerJp={headerJp}
                  inputWidth='170px' 
                  placeholder='ファイルから取込'
                  setResultMessage={setResultMessage}
                />
              }
              {/* 自社以外または権限区分が「0:閲覧のみ」以外は表示 */}
              {isUpload && onFileUpload && loginUserInfo?.authType !== AuthType.ReadOnly && (
                  <UploadButton
                    onClick={onFileUpload}
                    isLoading={isUploading}
                    inputWidth="170px"
                    placeholder="ファイルをアップロードして登録"
                    controlID="master-search-upload"
                  />
                )}
              {/* 発注推奨数ボタン */}
              {isOrderRecommend && handleOrderRecommendClick && loginUserInfo?.authType !== AuthType.ReadOnly && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleOrderRecommendClick}
                    disabled={disableRegisterAndOrderRecommend}
                  >
                    {optimizeButtonText}
                  </Button>
                )}
              
                <Typography variant="small" justifyContent="flex-end" alignItems="flex-end" display="flex" color="royalblue" marginRight='10px'>
                  { 
                  lastUpdateDatetime !=null && `最終更新日: ${convertFromDateTime(lastUpdateDatetime)}`
                  }
                </Typography>
            </Box>

          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={showDiscardChangeDialog}
        onClose={handleDiscardChangeDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">変更破棄の確認</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            編集中の内容があります。変更を破棄して検索しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardChangeDialogClose} color="info">キャンセル</Button>
          <Button variant="contained" onClick={handleReload} autoFocus>
            破棄して検索
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showForbiddenDownloadDialog}
        onClose={handleForbiddenDownloadDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">ダウンロード</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            データを編集中はダウンロードできません。内容をクリアするか反映してから実行してください
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleForbiddenDownloadDialogClose}>OK</Button>
        </DialogActions>
      </Dialog>
      {/* 発注推奨数ダイアログ（未使用のためコメントアウト）
      <Dialog
        open={showOrderRecommendDialog}
        onClose={handleOrderRecommendDialogClose}
        ...
      >
        発注推奨数: 保存して再計算しますか？
      </Dialog>
      <Dialog
        open={showOrderRecommendDiscardDialog}
        onClose={handleOrderRecommendDiscardDialogClose}
        ...
      >
        発注推奨数: 編集を破棄して計算しますか？
      </Dialog>
      */}
    </Paper>
  )
}

export default MasterSearchFrom
